/**
 * The phone app's pushes, for every page (staff and the client portal): a tapped push opens its page; a push
 * arriving with the app open shows as a banner here (Android hands it to the app instead of showing it); at
 * start (and with a new token) the token on the server follows what the person wants and the phone allows.
 * Nothing in a browser, nothing in an older app without push.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, X } from 'lucide-react'
import { useAuthStore } from '../../store/auth'
import { appBridge } from './bridge'
import { openPushPath, syncPush } from './push'
import { useT } from '../../i18n'

type Arrived = { title: string; body: string; path: string }
const SHOWN_MS = 10_000

export default function PushBridge() {
  const navigate = useNavigate()
  const identityId = useAuthStore(s => s.identityId)
  const t = useT()
  const [arrived, setArrived] = useState<Arrived | null>(null)
  useEffect(() => {
    const app = appBridge()
    if (!app?.features.push || !app.onPushTap) return
    return app.onPushTap(path => { void openPushPath(path, to => navigate(to)) })
  }, [])
  useEffect(() => {
    const app = appBridge()
    if (!app?.features.push || !app.onPushReceived) return
    return app.onPushReceived(push => setArrived(push))
  }, [])
  useEffect(() => {
    if (!arrived) return
    const timer = setTimeout(() => setArrived(null), SHOWN_MS)
    return () => clearTimeout(timer)
  }, [arrived])
  useEffect(() => {
    const app = appBridge()
    if (!identityId || !app?.features.push || !app.onPushToken) return
    const channel = t('profile.push.channel')
    void syncPush(channel)
    return app.onPushToken(() => { void syncPush(channel) })
  }, [identityId])
  if (!arrived) return null
  return (
    <div data-push-banner role="status" style={{
      position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 8px)', left: 8, right: 8, zIndex: 1000,
      display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 12,
      background: 'var(--bg-2)', border: '1px solid var(--line)', boxShadow: '0 6px 24px rgb(0 0 0 / .25)',
    }}>
      <button type="button" onClick={() => { const p = arrived.path; setArrived(null); if (p) void openPushPath(p, to => navigate(to)) }}
        style={{ flex: 1, minWidth: 0, display: 'flex', gap: 8, alignItems: 'flex-start', background: 'none', border: 'none', color: 'inherit', textAlign: 'left', padding: 0, cursor: 'pointer' }}>
        <Bell size={16} aria-hidden style={{ marginTop: 2, flexShrink: 0 }} />
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontWeight: 600, fontSize: 13, overflowWrap: 'anywhere' }}>{arrived.title}</span>
          <span style={{ display: 'block', fontSize: 13, color: 'var(--fg-2)', overflowWrap: 'anywhere' }}>{arrived.body}</span>
        </span>
      </button>
      <button type="button" className="btn btn-ghost btn-sm" aria-label={t('common.close')} onClick={() => setArrived(null)}><X size={14} /></button>
    </div>
  )
}
